/**
 * #141 — на чём основан подбор KFS и как это показать, не выдавая догадку за
 * правило.
 *
 * Сервер (`candidates.kfs_geo`) отдаёт `kfsGeoBasis`:
 * - `employer` — география работодателя известна: KFS подобран ПО ПРАВИЛУ;
 * - `residence_fallback` — работающий, но работодатель не указан: KFS
 *   подобран по месту жительства. Это ДОГАДКА (люди чаще работают рядом с
 *   домом), робот ею не пользуется — экран обязан её пометить;
 * - `unknown` — ни работодателя, ни прописки: KFS не предлагается вовсе;
 * - `not_applicable` — работодателя нет (безработный, студент): KFS не для
 *   него.
 *
 * ⚠️ Сами правила здесь НЕ повторяются: кого подобрать, решает сервер. Клиент
 * только раскладывает уже подобранный список на «точно» и «догадка» и
 * упорядочивает его.
 */

export type KfsGeoBasis =
  | 'employer'
  | 'residence_fallback'
  | 'unknown'
  | 'not_applicable';

export const KFS_BASIS_EMPLOYER: KfsGeoBasis = 'employer';
export const KFS_BASIS_RESIDENCE: KfsGeoBasis = 'residence_fallback';
export const KFS_BASIS_UNKNOWN: KfsGeoBasis = 'unknown';

interface IPlace {
  name?: string | null;
}

interface IGeoCandidate {
  powiat?: IPlace | null;
  voivodeship?: IPlace | null;
  kfsGeoBasis?: string | null;
}

interface IMatchedOperator {
  id: string;
  programType?: string | null;
  coverageScope?: string | null;
  currentIntake?: { startsOn?: string | null } | null;
}

/**
 * Что известно о географии — ОТДЕЛЬНО для двух программ.
 *
 * BUR смотрит на прописку человека, KFS — на работодателя (по `kfsGeoBasis`).
 * Прежний общий `geoKnown` склеивал четыре поля через «или», и прописка
 * делала «известной» географию KFS, которой не было.
 */
export const candidateGeo = (candidate?: IGeoCandidate | null) => {
  const basis = (candidate?.kfsGeoBasis || '') as KfsGeoBasis | '';
  const bur = Boolean(candidate?.powiat?.name || candidate?.voivodeship?.name);
  const kfs = basis === KFS_BASIS_EMPLOYER || basis === KFS_BASIS_RESIDENCE;
  return {
    basis,
    bur,
    kfs,
    /** Сузил ли сервер хоть что-то: без этого число «подошло» не значит ничего. */
    any: bur || kfs,
    kfsGuess: basis === KFS_BASIS_RESIDENCE,
    kfsUnknown: basis === KFS_BASIS_UNKNOWN,
  };
};

/**
 * KFS-строка, подобранная по месту жительства, а не по работодателю.
 *
 * Общепольский оператор догадкой не считается: он подходит при любом
 * работодателе, и место жительства на него не влияло.
 */
export const isKfsGuess = (
  operator: IMatchedOperator,
  basis?: string | null,
): boolean =>
  basis === KFS_BASIS_RESIDENCE &&
  operator.programType === 'kfs' &&
  operator.coverageScope !== 'nationwide';

/**
 * Сколько KFS-догадок по месту жительства в подборе.
 *
 * Прод-кейс #1309: `residence_fallback`, но в районе проживания KFS-операторов
 * нет вовсе (в łódzkie — ни одного). Тогда догадок ноль, и писать «KFS
 * подобран по месту жительства» — неправда: подобрать было некого.
 */
export const countKfsGuesses = (
  operators: IMatchedOperator[],
  basis?: string | null,
): number => operators.filter((operator) => isKfsGuess(operator, basis)).length;

/**
 * Порядок «Подходят по правилам».
 *
 * 1. Точные совпадения (BUR по прописке, KFS по работодателю) — раньше
 *    догадок KFS по месту жительства: догадка первой строкой читалась как
 *    лучший вариант («Gdański Urząd Pracy» у жителя Łódzkiego).
 * 2. Внутри группы — у кого ближайший набор (`currentIntake.startsOn`: идущий
 *    стартовал раньше будущего, поэтому он и идёт первым).
 * 3. Без набора — в серверном порядке (по алфавиту). Сортировка стабильная.
 */
export const orderMatchedOperators = <T extends IMatchedOperator>(
  operators: T[],
  basis?: string | null,
): T[] => {
  const startKey = (operator: T) => operator.currentIntake?.startsOn || '￿';
  return operators
    .map((operator, index) => ({ operator, index }))
    .sort((a, b) => {
      const guessA = isKfsGuess(a.operator, basis) ? 1 : 0;
      const guessB = isKfsGuess(b.operator, basis) ? 1 : 0;
      if (guessA !== guessB) return guessA - guessB;
      const startA = startKey(a.operator);
      const startB = startKey(b.operator);
      if (startA !== startB) return startA < startB ? -1 : 1;
      return a.index - b.index;
    })
    .map(({ operator }) => operator);
};

/**
 * Ключ причины подбора: `match.reason.<key>`.
 *
 * Догадка в счёт «подошло по правилам» НЕ входит: её число называется
 * отдельно и своими словами.
 */
export const matchReason = (
  operators: IMatchedOperator[],
  candidate?: IGeoCandidate | null,
) => {
  const geo = candidateGeo(candidate);
  const guesses = countKfsGuesses(operators, geo.basis);
  const exact = operators.length - guesses;
  // #1309: подбор по месту жительства, а KFS там нет ни одного — причина
  // обязана сказать об этом, а не «подошло N», как будто KFS проверен.
  const residenceNoKfs = geo.kfsGuess && guesses === 0;
  let key: string;
  if (!geo.any) key = 'noGeo';
  else if (residenceNoKfs)
    key = operators.length === 0 ? 'noneResidenceNoKfs' : 'residenceNoKfs';
  else if (operators.length === 0) key = 'none';
  else if (guesses > 0) key = 'withResidenceGuess';
  else if (exact > 1) key = 'several';
  else key = 'one';
  return { key, exact, guesses };
};
